import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { formatMonetaryAmount } from "@/utils/numbers.utils";
import { formatPaymentDate } from "@/utils/date.utils";
import {
  ContentCard,
  InfoSection,
  InfoRow,
  StatusHeader,
  StatusType,
} from "@/components/ui/data-display";
import { PaymentSuccessResponse } from "./types";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";

interface PaymentSuccessProps {
  data: PaymentSuccessResponse;
  paymentMethodLast4: string;
  currency: string;
  onDone: () => void;
}

export function PaymentSuccess({
  data,
  paymentMethodLast4,
  currency,
  onDone,
}: PaymentSuccessProps) {
  const { t } = useTranslation();

  // Format the date for display
  const formattedDate = formatPaymentDate(data.paidAt);

  // const totalAmount = data.amount + data.tipAmount;
  const totalMezzaCredits = data.cashBackUsed + data.walletUsed;
  const creditCardAmount =
    data.amount + data.tipAmount - data.cashBackUsed - data.walletUsed;

  return (
    <ContentCard
      headerContent={
        <StatusHeader
          type={StatusType.SUCCESS}
          title={t("payment.success.title")}
        />
      }
      description={t("payment.success.description")}
    >
      {/* Payment Details Section */}
      <InfoSection title={t("payment.details")}>
        <InfoRow
          label={t("payment.amount")}
          value={
            <CurrencyAmount
              amount={formatMonetaryAmount(data.amount)}
              currency={currency}
            />
          }
          description={{
            label: t("payment.excludingTip"),
          }}
          isNumeric
        />
        {/* <InfoRow
          label={t("payment.tip")}
          value={
            <CurrencyAmount
              amount={formatMonetaryAmount(data.tipAmount)}
              currency={currency}
            />
          }
          isNumeric
        />
        <InfoRow
          label={t("payment.total")}
          value={
            <CurrencyAmount
              amount={formatMonetaryAmount(totalAmount)}
              currency={currency}
            />
          }
          isHighlighted
          isNumeric
        /> */}
      </InfoSection>

      <Separator />

      {/* Payment Method Section */}
      <InfoSection title={t("payment.breakdown")}>
        {totalMezzaCredits > 0 && (
          <InfoRow
            label={t("payment.mezzaCredits")}
            value={
              <CurrencyAmount
                amount={formatMonetaryAmount(totalMezzaCredits)}
                currency={currency}
              />
            }
            isNumeric
          />
        )}

        <InfoRow
          label={`${t("payment.method")} (${paymentMethodLast4})`}
          value={
            <CurrencyAmount
              amount={formatMonetaryAmount(creditCardAmount)}
              currency={currency}
            />
          }
          // description={{
          //   label: t("payment.includesTip", { tipAmount: formatMonetaryAmount(data.tipAmount) }),
          // }}
          isNumeric
        />
      </InfoSection>

      <Separator />

      {/* Rewards Section */}
      <InfoSection title={t("payment.rewards")} className="bg-green-50 rounded-lg p-4 space-y-2">
        <InfoRow
          label={t("payment.cashbackEarned")}
          value={
            <CurrencyAmount
              amount={formatMonetaryAmount(data.creditBackEarned ?? 0)}
              currency={currency}
            />
          }
          isNumeric
        />
      </InfoSection>

      <Separator />

      {/* Receipt Details Section */}
      <InfoSection title={t("payment.receipt")} className="bg-muted-foreground/10 rounded-lg p-4">
        <InfoRow
          label={t("payment.orderID")}
          value={`#${data.simpleIdentifier}`}
          isHighlighted        
        />
        <InfoRow label={t("payment.date")} value={formattedDate} />
        <InfoRow
          label={t("payment.status")}
          value={data.status}
          valueClassName="text-green-600"
        />
      </InfoSection>

      <Button onClick={onDone} className="w-full mt-4">
        {t("payment.success.done")}
      </Button>
    </ContentCard>
  );
}
